import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { Snackbar, Alert, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';

interface ToastContextType {
    showError: (message: string, fullErrorData?: any) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

interface ToastProviderProps {
    children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [fullErrorData, setFullErrorData] = useState<any>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const showError = useCallback((errorMessage: string, errorData?: any) => {
        setMessage(errorMessage);
        setFullErrorData(errorData);
        setOpen(true);
    }, []);

    const handleClose = () => {
        setOpen(false);
        setFullErrorData(null);
    };

    const handleCopy = () => {
        const copyData = fullErrorData ? JSON.stringify(fullErrorData, null, 2) : message;
        navigator.clipboard.writeText(copyData);
    };

    return (
        <ToastContext.Provider value={{ showError }}>
            {children}
            <Snackbar
                open={open}
                autoHideDuration={isMobile ? 3000 : null}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleClose}
                    severity="error"
                    action={
                        !isMobile ? (
                            <IconButton
                                size="small"
                                onClick={handleCopy}
                                color="inherit"
                            >
                                <ContentCopy fontSize="small" />
                            </IconButton>
                        ) : undefined
                    }
                >
                    {message}
                </Alert>
            </Snackbar>
        </ToastContext.Provider>
    );
};